import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import SpinWheel from '@/components/SpinWheel';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { Settings, QrCode, Users, BarChart3, Shield, Plus, Trash2, Download, Search, LogOut, Check, X, ChevronLeft, ChevronRight, Printer, Send, Mail, MessageSquare, Sparkles, CreditCard, Loader2, ShieldCheck } from 'lucide-react';
import ImageUpload from '@/components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHEEL_COLORS = ['#7C3AED', '#F59E0B', '#10B981', '#EF4444', '#3B82F6', '#EC4899', '#14B8A6', '#8B5CF6', '#F97316', '#06B6D4', '#84CC16', '#E11D48'];

export default function MerchantDashboard() {
  const { user, logout, axiosAuth, token } = useAuth();
  const navigate = useNavigate();
  const qrRef = useRef(null);
  const posterRef = useRef(null);
  const [tab, setTab] = useState('wheel');
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerMeta, setCustomerMeta] = useState({ total: 0, page: 1, pages: 0 });
  const [pendingCodes, setPendingCodes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [validateInput, setValidateInput] = useState('');
  // Campaign states
  const [credits, setCredits] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [campType, setCampType] = useState('email');
  const [campTopic, setCampTopic] = useState('');
  const [campPromo, setCampPromo] = useState('');
  const [campTone, setCampTone] = useState('professionnel et engageant');
  const [campSubject, setCampSubject] = useState('');
  const [campContent, setCampContent] = useState('');
  const [campGenerating, setCampGenerating] = useState(false);
  const [campGenStep, setCampGenStep] = useState(0);
  const [campSending, setCampSending] = useState(false);
  const [campSelectAll, setCampSelectAll] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [campImages, setCampImages] = useState(['']);
  const [profile, setProfile] = useState({ address: '', logo_url: '', business_phone: '' });

  const api = useCallback(() => axiosAuth(), [axiosAuth]);

  const fetchConfig = useCallback(async () => {
    try { const { data } = await api().get('/merchant/config'); setConfig(data); } catch (e) { console.error(e); }
  }, [api]);
  const fetchStats = useCallback(async () => {
    try { const { data } = await api().get('/merchant/stats'); setStats(data); } catch (e) { console.error(e); }
  }, [api]);
  const fetchCustomers = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set('search', search);
      const { data } = await api().get(`/merchant/customers?${params}`);
      setCustomers(data.customers); setCustomerMeta({ total: data.total, page: data.page, pages: data.pages });
    } catch (e) { console.error(e); }
  }, [api, search]);
  const fetchPendingCodes = useCallback(async () => {
    try { const { data } = await api().get('/merchant/pending-codes'); setPendingCodes(data.codes); } catch (e) { console.error(e); }
  }, [api]);
  const fetchCredits = useCallback(async () => {
    try { const { data } = await api().get('/merchant/credits'); setCredits(data.balance); } catch (e) { console.error(e); }
  }, [api]);
  const fetchCampaigns = useCallback(async () => {
    try { const { data } = await api().get('/merchant/campaigns'); setCampaigns(data.campaigns); } catch (e) { console.error(e); }
  }, [api]);
  const fetchProfile = useCallback(async () => {
    try { const { data } = await api().get('/merchant/profile'); setProfile(data); } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { fetchConfig(); fetchStats(); fetchCustomers(); fetchPendingCodes(); fetchCredits(); fetchCampaigns(); fetchProfile(); }, [fetchConfig, fetchStats, fetchCustomers, fetchPendingCodes, fetchCredits, fetchCampaigns, fetchProfile]);

  const saveConfig = async () => {
    if (!config) return;
    const total = config.slots.reduce((s, sl) => s + sl.probability, 0);
    if (Math.abs(total - 100) > 0.1) { toast.error(`Les probabilités doivent totaliser 100% (actuellement ${total.toFixed(1)}%)`); return; }
    setSaving(true);
    try { await api().put('/merchant/config', config); toast.success('Configuration sauvegardée !'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur de sauvegarde'); }
    finally { setSaving(false); }
  };

  const addSlot = () => {
    if (!config || config.slots.length >= 12) return;
    setConfig(prev => ({ ...prev, slots: [...prev.slots, { id: Date.now().toString(36), label: 'Nouveau lot', color: WHEEL_COLORS[prev.slots.length % WHEEL_COLORS.length], probability: 0, is_losing: false, validity_hours: 24 }] }));
  };
  const removeSlot = (idx) => setConfig(prev => ({ ...prev, slots: prev.slots.filter((_, i) => i !== idx) }));
  const updateSlot = (idx, key, value) => setConfig(prev => { const s = [...prev.slots]; s[idx] = { ...s[idx], [key]: value }; return { ...prev, slots: s }; });

  const handleValidateCode = async () => {
    if (!validateInput.trim()) return;
    try {
      const { data } = await api().post(`/merchant/validate-code/${validateInput.trim()}`);
      toast.success(`Code validé ! Prix: ${data.prize} — Client: ${data.customer}`);
      setValidateInput(''); fetchPendingCodes(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Code invalide'); }
  };

  const exportCSV = async () => {
    try {
      const { data } = await api().get('/merchant/customers/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a'); a.href = url; a.download = 'clients_spin-review.csv'; a.click();
    } catch (e) { toast.error('Erreur export'); }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a'); a.href = canvas.toDataURL('image/png'); a.download = 'spin-review-qrcode.png'; a.click();
  };

  const printPoster = () => {
    const el = posterRef.current;
    if (!el) return;
    // Convert canvas QR codes to images before printing
    const clone = el.cloneNode(true);
    const canvases = el.querySelectorAll('canvas');
    const cloneCanvases = clone.querySelectorAll('canvas');
    canvases.forEach((c, i) => {
      const img = document.createElement('img');
      img.src = c.toDataURL('image/png');
      img.style.width = c.style.width || c.width + 'px';
      img.style.height = c.style.height || c.height + 'px';
      cloneCanvases[i]?.parentNode?.replaceChild(img, cloneCanvases[i]);
    });
    // Build HTML in memory then load via Blob URL (safer than document.write, no arbitrary script exec)
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Spin Review - Affiche</title>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@700;900&family=Poppins:wght@400;600;700&display=swap" rel="stylesheet">
      <style>*{margin:0;padding:0;box-sizing:border-box}body{display:flex;justify-content:center;align-items:center;min-height:100vh;background:#fff}
      @media print{body{background:#fff}@page{size:A5 portrait;margin:0}}</style></head><body>${clone.innerHTML}<script>window.addEventListener('load',()=>{setTimeout(()=>window.print(),400);});</script></body></html>`;
    const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const w = window.open(blobUrl, '_blank');
    if (w) setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  };

  const handleLogout = () => { navigate('/'); setTimeout(() => logout(), 100); };

  const generateContent = async () => {
    if (!campTopic.trim()) { toast.error('Indiquez le thème de la campagne'); return; }
    setCampGenerating(true);
    setCampGenStep(1);
    const stepTimer1 = setTimeout(() => setCampGenStep(2), 2000);
    const stepTimer2 = setTimeout(() => setCampGenStep(3), 5000);
    const stepTimer3 = setTimeout(() => setCampGenStep(4), 9000);
    try {
      const imageUrls = campImages.filter(u => u.trim());
      const { data } = await api().post('/merchant/campaign/generate', {
        type: campType, topic: campTopic, tone: campTone, promo_details: campPromo,
        image_urls: imageUrls
      });
      setCampContent(data.content);
      toast.success('Contenu généré par l\'IA !');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de génération'); }
    finally { clearTimeout(stepTimer1); clearTimeout(stepTimer2); clearTimeout(stepTimer3); setCampGenerating(false); setCampGenStep(0); }
  };

  const saveProfile = async () => {
    try {
      await api().put('/merchant/profile', profile);
      toast.success('Profil mis à jour !');
    } catch (e) { toast.error('Erreur'); }
  };

  const sendCampaign = async () => {
    if (!campContent.trim()) { toast.error('Aucun contenu à envoyer'); return; }
    if (campType === 'email' && !campSubject.trim()) { toast.error('Ajoutez un objet d\'email'); return; }
    setCampSending(true);
    try {
      const allCustomers = await api().get('/merchant/customers?limit=1000');
      const recipients = allCustomers.data.customers.filter(c => campType === 'email' ? c.email : c.phone);
      if (recipients.length === 0) { toast.error(`Aucun client avec ${campType === 'email' ? 'email' : 'téléphone'}`); return; }
      const { data } = await api().post('/merchant/campaign/send', {
        type: campType, recipients, subject: campSubject, content: campContent
      });
      toast.success(`${data.sent} ${campType === 'email' ? 'emails' : 'SMS'} envoyés ! Coût : ${data.cost.toFixed(2)}€`);
      setCredits(data.new_balance);
      fetchCampaigns();
      setCampContent(''); setCampSubject(''); setCampTopic(''); setCampPromo('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur d\'envoi'); }
    finally { setCampSending(false); }
  };

  const buyCredits = async (amount) => {
    try {
      const { data } = await api().post('/merchant/credits/buy', { amount, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error('Erreur de paiement'); }
  };

  const sendTestEmail = async () => {
    if (!testEmail.trim()) { toast.error('Entrez une adresse email'); return; }
    if (!campContent.trim()) { toast.error('Générez d\'abord le contenu avec l\'IA'); return; }
    setSendingTest(true);
    try {
      const { data } = await api().post('/merchant/campaign/send-test', {
        email: testEmail, subject: campSubject || 'Test Spin Review', content: campContent, type: campType
      });
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur d\'envoi test'); }
    finally { setSendingTest(false); }
  };
  const probTotal = config?.slots?.reduce((s, sl) => s + (sl.probability || 0), 0) || 0;
  const spinUrl = `${window.location.origin}/spin/${user?.id}`;
  const prizes = config?.slots?.filter(s => !s.is_losing).map(s => s.label) || [];

  return (
    <div className="min-h-screen" style={{ background: '#0F0F1A' }}>
      {/* Top Bar */}
      <div className="border-b border-white/10 bg-[#0F0F1A]/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-13 sm:h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center font-bold text-xs">S</div>
            <span className="font-bold text-white hidden sm:block" style={{ fontFamily: 'Outfit' }}>Spin Review</span>
            <Badge variant="outline" className="border-[#7C3AED]/30 text-[#7C3AED] text-[10px] sm:text-xs max-w-[120px] sm:max-w-none truncate">{user?.business_name || 'Dashboard'}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#94A3B8] hidden sm:block">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={() => navigate('/vendor/safe-review')} className="text-emerald-400 hover:bg-emerald-400/10 gap-1.5 h-8 px-2" data-testid="merchant-safe-review-btn">
              <ShieldCheck className="w-4 h-4" /> <span className="hidden sm:inline text-xs">Safe Review</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#94A3B8] hover:text-white h-8 w-8 p-0" data-testid="logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5" data-testid="merchant-stats">
          {[
            { label: 'Total spins', value: stats?.total_spins || 0, color: '#7C3AED' },
            { label: 'Prix distribués', value: stats?.total_prizes || 0, color: '#F59E0B' },
            { label: 'Prix populaire', value: stats?.most_popular || 'N/A', color: '#10B981', text: true },
            { label: 'Codes en attente', value: stats?.pending_codes || 0, color: '#EF4444' }
          ].map((s, i) => (
            <div key={i} className="rounded-xl p-3 sm:p-4" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
              <p className="text-[10px] sm:text-xs text-[#94A3B8] mb-0.5">{s.label}</p>
              <p className={`${s.text ? 'text-sm sm:text-lg' : 'text-xl sm:text-2xl'} font-bold truncate`} style={{ color: s.color, fontFamily: 'Outfit' }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0 mb-5">
            <TabsList className="bg-[#1A1A2E] border border-white/5 h-auto p-1 inline-flex w-auto min-w-max">
              {[
                { v: 'wheel', icon: Settings, l: 'Roue' },
                { v: 'social', icon: Users, l: 'Actions' },
                { v: 'qr', icon: QrCode, l: 'QR Code' },
                { v: 'customers', icon: BarChart3, l: 'Clients' },
                { v: 'anticheat', icon: Shield, l: 'Anti-Triche' },
              { v: 'campaigns', icon: Send, l: 'Campagnes' }
              ].map(t => (
                <TabsTrigger key={t.v} value={t.v} className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white gap-1 text-xs whitespace-nowrap px-3 py-1.5" data-testid={`tab-${t.v}`}>
                  <t.icon className="w-3.5 h-3.5" /> {t.l}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* ═══ Wheel Config Tab ═══ */}
          <TabsContent value="wheel" data-testid="wheel-config-tab">
            <div className="grid lg:grid-cols-2 gap-5">
              <div className="rounded-xl p-4 sm:p-6" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Configuration des lots</h3>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-mono ${Math.abs(probTotal - 100) < 0.1 ? 'text-[#10B981]' : 'text-[#EF4444]'}`} data-testid="prob-total">{probTotal.toFixed(1)}%</span>
                    <Button size="sm" onClick={addSlot} disabled={!config || config.slots?.length >= 12} className="btn-primary text-xs h-7 px-2" data-testid="add-slot-btn"><Plus className="w-3 h-3 mr-1" />Lot</Button>
                  </div>
                </div>
                <div className="space-y-2 max-h-[450px] overflow-y-auto">
                  {config?.slots?.map((slot, idx) => (
                    <div key={slot.id || idx} className="p-3 rounded-lg bg-white/5 border border-white/5" data-testid={`slot-${idx}`}>
                      {/* Row 1: color + label + delete */}
                      <div className="flex items-center gap-2 mb-2">
                        <input type="color" value={slot.color} onChange={e => updateSlot(idx, 'color', e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent flex-shrink-0" />
                        <Input value={slot.label} onChange={e => updateSlot(idx, 'label', e.target.value)} className="flex-1 bg-transparent border-white/10 text-white text-sm h-8" placeholder="Nom du lot" />
                        <Button variant="ghost" size="sm" onClick={() => removeSlot(idx)} className="text-[#EF4444] hover:text-red-300 h-8 w-8 p-0 flex-shrink-0"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                      {/* Row 2: probability + losing + validity */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-1">
                          <Input type="number" value={slot.probability} onChange={e => updateSlot(idx, 'probability', parseFloat(e.target.value) || 0)}
                            className="w-16 bg-transparent border-white/10 text-white text-sm h-7 text-center" min="0" max="100" step="0.1" />
                          <span className="text-xs text-[#94A3B8]">%</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-[#94A3B8]">Perdu</span>
                          <Switch checked={slot.is_losing} onCheckedChange={v => updateSlot(idx, 'is_losing', v)} className="data-[state=checked]:bg-[#EF4444] scale-90" />
                        </div>
                        {!slot.is_losing && (
                          <div className="flex items-center gap-1">
                            <Input type="number" value={slot.validity_hours || 24} onChange={e => updateSlot(idx, 'validity_hours', parseInt(e.target.value) || 0)}
                              className="w-14 bg-transparent border-white/10 text-white text-sm h-7 text-center" min="0" />
                            <span className="text-[10px] text-[#94A3B8]">h</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Button onClick={saveConfig} disabled={saving} className="btn-primary rounded-xl w-full mt-4" data-testid="save-config-btn">
                  {saving ? 'Sauvegarde...' : 'Sauvegarder la configuration'}
                </Button>
              </div>
              <div className="rounded-xl p-4 sm:p-6 flex flex-col items-center justify-center" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
                <h3 className="text-base sm:text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit' }}>Aperçu en direct</h3>
                <SpinWheel slots={config?.slots || []} size={Math.min(260, typeof window !== 'undefined' ? window.innerWidth - 80 : 260)} onSpinEnd={() => {}} preview={true} />
                <p className="text-xs text-[#94A3B8] mt-3">{config?.slots?.length || 0} lots configurés</p>
              </div>
            </div>
          </TabsContent>

          {/* ═══ Social Actions Tab ═══ */}
          <TabsContent value="social" data-testid="social-config-tab">
            <div className="grid lg:grid-cols-2 gap-5">
            <div className="rounded-xl p-4 sm:p-6" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
              <h3 className="text-base sm:text-lg font-semibold text-white mb-5" style={{ fontFamily: 'Outfit' }}>Actions sociales requises</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-[#E2E8F0] text-sm mb-2 block">Action requise avant de tourner la roue</Label>
                  <Select value={config?.social_action || 'google'} onValueChange={v => setConfig(p => ({ ...p, social_action: v }))}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="social-action-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A2E] border-white/10">
                      <SelectItem value="google" className="text-white">Avis Google</SelectItem>
                      <SelectItem value="instagram" className="text-white">Suivre sur Instagram</SelectItem>
                      <SelectItem value="facebook" className="text-white">Suivre sur Facebook</SelectItem>
                      <SelectItem value="choice" className="text-white">Le client choisit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#E2E8F0] text-sm">Lien avis Google</Label>
                  <Input value={config?.google_review_url || ''} onChange={e => setConfig(p => ({ ...p, google_review_url: e.target.value }))}
                    placeholder="https://g.page/r/..." className="mt-1 bg-white/5 border-white/10 text-white" data-testid="google-url-input" />
                </div>
                <div>
                  <Label className="text-[#E2E8F0] text-sm">URL Instagram</Label>
                  <Input value={config?.instagram_url || ''} onChange={e => setConfig(p => ({ ...p, instagram_url: e.target.value }))}
                    placeholder="https://instagram.com/votre-page" className="mt-1 bg-white/5 border-white/10 text-white" data-testid="instagram-url-input" />
                </div>
                <div>
                  <Label className="text-[#E2E8F0] text-sm">URL Facebook</Label>
                  <Input value={config?.facebook_url || ''} onChange={e => setConfig(p => ({ ...p, facebook_url: e.target.value }))}
                    placeholder="https://facebook.com/votre-page" className="mt-1 bg-white/5 border-white/10 text-white" data-testid="facebook-url-input" />
                </div>
                <div>
                  <Label className="text-[#E2E8F0] text-sm">Informations à collecter</Label>
                  <Select value={config?.collect_field || 'phone'} onValueChange={v => setConfig(p => ({ ...p, collect_field: v }))}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1" data-testid="collect-field-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A2E] border-white/10">
                      <SelectItem value="phone" className="text-white">Téléphone uniquement</SelectItem>
                      <SelectItem value="email" className="text-white">Email uniquement</SelectItem>
                      <SelectItem value="both" className="text-white">Téléphone + Email</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#94A3B8] mt-1">Ces champs apparaîtront dans le formulaire du client</p>
                </div>
                <Button onClick={saveConfig} disabled={saving} className="btn-primary rounded-xl w-full" data-testid="save-social-btn">{saving ? 'Sauvegarde...' : 'Sauvegarder'}</Button>
              </div>
            </div>
            {/* Profile */}
            <div className="rounded-xl p-4 sm:p-6" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
              <h3 className="text-base sm:text-lg font-semibold text-white mb-5" style={{ fontFamily: 'Outfit' }}>Informations du commerce</h3>
              <p className="text-xs text-[#94A3B8] mb-4">Ces informations seront utilisées dans vos emails promotionnels</p>
              <div className="space-y-4">
                <div>
                  <Label className="text-[#E2E8F0] text-sm">Adresse du commerce</Label>
                  <Input value={profile.address} onChange={e => setProfile(p => ({ ...p, address: e.target.value }))}
                    placeholder="12 rue de la Paix, 75001 Paris" className="mt-1 bg-white/5 border-white/10 text-white" data-testid="profile-address" />
                </div>
                <div>
                  <Label className="text-[#E2E8F0] text-sm">Téléphone du commerce</Label>
                  <Input value={profile.business_phone} onChange={e => setProfile(p => ({ ...p, business_phone: e.target.value }))}
                    placeholder="01 23 45 67 89" className="mt-1 bg-white/5 border-white/10 text-white" data-testid="profile-phone" />
                </div>
                <div>
                  <ImageUpload value={profile.logo_url} onChange={url => setProfile(p => ({ ...p, logo_url: url }))}
                    label="Logo du commerce" token={token} />
                </div>
                <Button onClick={saveProfile} className="btn-primary rounded-xl w-full">Sauvegarder le profil</Button>
              </div>
            </div>
            </div>
          </TabsContent>

          {/* ═══ QR Code + Poster Tab ═══ */}
          <TabsContent value="qr" data-testid="qr-tab">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Simple QR */}
              <div className="rounded-xl p-6 text-center" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
                <h3 className="text-base sm:text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Votre QR Code</h3>
                <p className="text-sm text-[#94A3B8] mb-5">Imprimez-le et placez-le dans votre commerce</p>
                <div ref={qrRef} className="inline-block p-4 bg-white rounded-xl mb-4">
                  <QRCodeCanvas value={spinUrl} size={180} fgColor="#0F0F1A" bgColor="#FFFFFF" level="H" data-testid="qr-code" />
                </div>
                <p className="text-[10px] text-[#94A3B8] mb-4 break-all px-2">{spinUrl}</p>
                <Button onClick={downloadQR} className="btn-accent rounded-xl gap-2 w-full sm:w-auto" data-testid="download-qr-btn"><Download className="w-4 h-4" /> Télécharger QR (PNG)</Button>
              </div>

              {/* A5 Poster */}
              <div className="rounded-xl p-6" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
                <h3 className="text-base sm:text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Affiche prête à imprimer</h3>
                <p className="text-sm text-[#94A3B8] mb-5">Format A5 — à poser sur votre comptoir ou vitrine</p>
                
                {/* Poster Preview */}
                <div ref={posterRef}>
                  <div style={{
                    width: '100%', maxWidth: '360px', margin: '0 auto', aspectRatio: '148/210',
                    background: 'linear-gradient(160deg, #0F0F1A 0%, #1A1A2E 40%, #0F0F1A 100%)',
                    borderRadius: '16px', overflow: 'hidden', position: 'relative',
                    border: '2px solid rgba(124,58,237,0.3)',
                    boxShadow: '0 0 40px rgba(124,58,237,0.15)'
                  }}>
                    {/* Top gradient bar */}
                    <div style={{ height: '6px', background: 'linear-gradient(90deg, #7C3AED, #F59E0B)' }} />
                    
                    <div style={{ padding: '24px 20px 20px', textAlign: 'center', position: 'relative' }}>
                      {/* Decorative circles */}
                      <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(124,58,237,0.08)' }} />
                      <div style={{ position: 'absolute', bottom: '-30px', left: '-30px', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(245,158,11,0.06)' }} />
                      
                      {/* Logo */}
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '16px', position: 'relative' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'linear-gradient(135deg, #7C3AED, #F59E0B)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '12px', color: 'white' }}>S</div>
                        <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '18px', color: 'white' }}>Spin Review</span>
                      </div>

                      {/* Headline */}
                      <h2 style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 900, fontSize: '26px', lineHeight: 1.1, color: 'white', marginBottom: '4px', position: 'relative' }}>
                        SCANNEZ
                      </h2>
                      <h2 style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 900, fontSize: '26px', lineHeight: 1.1, marginBottom: '8px', background: 'linear-gradient(135deg, #7C3AED, #F59E0B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', position: 'relative' }}>
                        & GAGNEZ !
                      </h2>
                      <p style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '16px', position: 'relative' }}>Tournez la roue et tentez de gagner des cadeaux</p>

                      {/* QR Code */}
                      <div style={{ display: 'inline-block', padding: '12px', background: 'white', borderRadius: '12px', marginBottom: '14px', position: 'relative' }}>
                        <QRCodeCanvas value={spinUrl} size={130} fgColor="#0F0F1A" bgColor="#FFFFFF" level="H" />
                      </div>

                      {/* Prizes */}
                      <div style={{ marginBottom: '14px', position: 'relative' }}>
                        <p style={{ fontSize: '10px', color: '#F59E0B', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>A gagner</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                          {prizes.slice(0, 4).map((p) => (
                            <span key={p.label || p.id || p.name} style={{ fontSize: '10px', padding: '3px 10px', borderRadius: '20px', background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.25)', color: '#E2E8F0', fontWeight: 600 }}>{p}</span>
                          ))}
                        </div>
                      </div>

                      {/* Business name */}
                      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '12px', position: 'relative' }}>
                        <p style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '14px', color: 'white' }}>{user?.business_name}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <Button onClick={printPoster} className="btn-primary rounded-xl gap-2 w-full mt-5" data-testid="print-poster-btn">
                  <Printer className="w-4 h-4" /> Imprimer l&apos;affiche A5
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* ═══ Customers Tab ═══ */}
          <TabsContent value="customers" data-testid="customers-tab">
            <div className="rounded-xl p-4 sm:p-6" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Données clients ({customerMeta.total})</h3>
                <div className="flex gap-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:flex-none">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                    <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchCustomers()}
                      placeholder="Rechercher..." className="pl-9 bg-white/5 border-white/10 text-white w-full sm:w-48 h-9" data-testid="customer-search-input" />
                  </div>
                  <Button onClick={exportCSV} variant="outline" size="sm" className="border-white/10 text-white hover:bg-white/5 gap-1 flex-shrink-0" data-testid="export-csv-btn"><Download className="w-3 h-3" /> CSV</Button>
                </div>
              </div>
              {/* Mobile card view */}
              <div className="sm:hidden space-y-2">
                {customers.length === 0 ? (
                  <p className="text-sm text-[#94A3B8] text-center py-6">Aucun client pour le moment</p>
                ) : customers.map((c) => (
                  <div key={c.id} className="p-3 rounded-lg bg-white/5 border border-white/5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-white font-medium">{c.first_name} {c.last_name}</span>
                      <span className={`text-xs font-semibold ${c.is_losing ? 'text-[#94A3B8]' : 'text-[#F59E0B]'}`}>{c.prize_won}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
                      <span>{new Date(c.created_at).toLocaleDateString('fr-FR')}</span>
                      <Badge variant="outline" className="text-[10px] border-[#7C3AED]/30 text-[#7C3AED] py-0">{c.action_performed}</Badge>
                    </div>
                    {(c.phone || c.email) && <p className="text-xs text-[#94A3B8] mt-1">{c.phone}{c.phone && c.email ? ' · ' : ''}{c.email}</p>}
                    {c.prize_code && <p className="text-xs text-[#7C3AED] font-mono mt-1">{c.prize_code} {c.code_validated ? '✓' : ''}</p>}
                  </div>
                ))}
              </div>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-[#94A3B8]">Date</TableHead>
                      <TableHead className="text-[#94A3B8]">Nom</TableHead>
                      <TableHead className="text-[#94A3B8]">Téléphone</TableHead>
                      <TableHead className="text-[#94A3B8]">Email</TableHead>
                      <TableHead className="text-[#94A3B8]">Action</TableHead>
                      <TableHead className="text-[#94A3B8]">Prix</TableHead>
                      <TableHead className="text-[#94A3B8]">Code</TableHead>
                      <TableHead className="text-[#94A3B8]">Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center text-[#94A3B8] py-8">Aucun client pour le moment</TableCell></TableRow>
                    ) : customers.map((c) => (
                      <TableRow key={c.id} className="border-white/5 hover:bg-white/5">
                        <TableCell className="text-[#E2E8F0] text-sm">{new Date(c.created_at).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell className="text-white text-sm font-medium">{c.first_name} {c.last_name}</TableCell>
                        <TableCell className="text-[#E2E8F0] text-sm">{c.phone || '-'}</TableCell>
                        <TableCell className="text-[#E2E8F0] text-sm">{c.email || '-'}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs border-[#7C3AED]/30 text-[#7C3AED]">{c.action_performed}</Badge></TableCell>
                        <TableCell className={`text-sm font-medium ${c.is_losing ? 'text-[#94A3B8]' : 'text-[#F59E0B]'}`}>{c.prize_won}</TableCell>
                        <TableCell className="text-[#7C3AED] text-sm font-mono">{c.prize_code || '-'}</TableCell>
                        <TableCell>{c.prize_code ? (c.code_validated ? <Check className="w-4 h-4 text-[#10B981]" /> : <X className="w-4 h-4 text-[#94A3B8]" />) : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {customerMeta.pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button variant="ghost" size="sm" disabled={customerMeta.page <= 1} onClick={() => fetchCustomers(customerMeta.page - 1)} className="text-white"><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-sm text-[#94A3B8]">{customerMeta.page} / {customerMeta.pages}</span>
                  <Button variant="ghost" size="sm" disabled={customerMeta.page >= customerMeta.pages} onClick={() => fetchCustomers(customerMeta.page + 1)} className="text-white"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ═══ Anti-Cheat Tab ═══ */}
          <TabsContent value="anticheat" data-testid="anticheat-tab">
            <div className="grid lg:grid-cols-2 gap-5">
              <div className="rounded-xl p-4 sm:p-6" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
                <h3 className="text-base sm:text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit' }}>Système anti-triche</h3>
                <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 mb-4">
                  <div>
                    <p className="text-sm text-white font-medium">Activer les codes uniques</p>
                    <p className="text-xs text-[#94A3B8] mt-0.5">Chaque lot gagnant génère un code à usage unique</p>
                  </div>
                  <Switch checked={config?.anti_cheat_enabled || false} onCheckedChange={v => setConfig(p => ({ ...p, anti_cheat_enabled: v }))} className="data-[state=checked]:bg-[#7C3AED]" data-testid="anticheat-toggle" />
                </div>
                <div className="p-3 rounded-lg mb-5" style={{ background: config?.anti_cheat_enabled ? 'rgba(124,58,237,0.1)' : 'rgba(245,158,11,0.1)', border: config?.anti_cheat_enabled ? '1px solid rgba(124,58,237,0.2)' : '1px solid rgba(245,158,11,0.2)' }}>
                  {config?.anti_cheat_enabled
                    ? <p className="text-xs text-[#E2E8F0]"><span className="font-semibold text-[#7C3AED]">Active :</span> Le client recoit un code unique. Vous le validez ici avant de remettre le lot.</p>
                    : <p className="text-xs text-[#E2E8F0]"><span className="font-semibold text-[#F59E0B]">Desactive :</span> Le client presente simplement son ecran. Pas de code a valider.</p>
                  }
                </div>
                <Button onClick={saveConfig} disabled={saving} className="btn-primary rounded-xl w-full mb-5" data-testid="save-anticheat-btn">Sauvegarder</Button>
                {config?.anti_cheat_enabled && (
                  <div>
                    <Label className="text-[#E2E8F0] text-sm mb-2 block">Valider un code client</Label>
                    <div className="flex gap-2">
                      <Input value={validateInput} onChange={e => setValidateInput(e.target.value.toUpperCase())} placeholder="SW-XXXXXX" className="bg-white/5 border-white/10 text-white font-mono uppercase" data-testid="validate-code-input" />
                      <Button onClick={handleValidateCode} className="btn-accent flex-shrink-0" data-testid="validate-code-btn">Valider</Button>
                    </div>
                  </div>
                )}
              </div>
              {config?.anti_cheat_enabled && (
                <div className="rounded-xl p-4 sm:p-6" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit' }}>Codes en attente</h3>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {pendingCodes.filter(c => !c.code_validated && !c.is_losing).length === 0
                      ? <p className="text-sm text-[#94A3B8] text-center py-4">Aucun code en attente</p>
                      : pendingCodes.filter(c => !c.code_validated && !c.is_losing).map((c) => (
                        <div key={c.id || c.prize_code} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                          <div>
                            <p className="text-sm text-[#7C3AED] font-mono font-bold">{c.prize_code}</p>
                            <p className="text-xs text-[#94A3B8]">{c.first_name} {c.last_name} — {c.prize_won}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-[#94A3B8]">{new Date(c.created_at).toLocaleDateString('fr-FR')}</p>
                            {c.code_expires_at && <p className="text-xs text-[#F59E0B]">Exp: {new Date(c.code_expires_at).toLocaleDateString('fr-FR')}</p>}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ═══ Campaigns Tab ═══ */}
          <TabsContent value="campaigns" data-testid="campaigns-tab">
            {/* Hero banner - give desire without obligation */}
            <div className="rounded-2xl p-5 sm:p-8 mb-5 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.12), rgba(245,158,11,0.08))', border: '1px solid rgba(124,58,237,0.15)' }}>
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10 -translate-y-1/2 translate-x-1/2" style={{ background: 'radial-gradient(circle, #F59E0B, transparent)' }} />
              <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center flex-shrink-0">
                  <Send className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-black text-white mb-1" style={{ fontFamily: 'Outfit' }}>
                    Boostez vos ventes avec des campagnes ciblées
                  </h3>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">
                    Vous avez récolté les coordonnées de vos clients grâce à la roue — pourquoi ne pas en profiter ?
                    Envoyez des <span className="text-[#7C3AED] font-medium">emails promotionnels</span> ou des <span className="text-[#F59E0B] font-medium">SMS percutants</span> rédigés
                    par une IA marketing, en quelques clics. <span className="text-white font-medium">C&apos;est optionnel</span>, mais les commerçants qui l&apos;utilisent
                    voient en moyenne <span className="text-[#10B981] font-bold">+35% de clients fidélisés</span>.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 mt-4 sm:ml-[72px]">
                <div className="flex items-center gap-2 text-xs text-[#E2E8F0]">
                  <div className="w-5 h-5 rounded-full bg-[#7C3AED]/20 flex items-center justify-center"><Mail className="w-3 h-3 text-[#7C3AED]" /></div>
                  Email dès 0,02€
                </div>
                <div className="flex items-center gap-2 text-xs text-[#E2E8F0]">
                  <div className="w-5 h-5 rounded-full bg-[#F59E0B]/20 flex items-center justify-center"><MessageSquare className="w-3 h-3 text-[#F59E0B]" /></div>
                  SMS dès 0,15€
                </div>
                <div className="flex items-center gap-2 text-xs text-[#E2E8F0]">
                  <div className="w-5 h-5 rounded-full bg-[#10B981]/20 flex items-center justify-center"><Sparkles className="w-3 h-3 text-[#10B981]" /></div>
                  IA qui rédige pour vous
                </div>
                <div className="flex items-center gap-2 text-xs text-[#E2E8F0]">
                  <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>
                  Aucun engagement
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-5">
              {/* Left: Campaign Creator */}
              <div className="lg:col-span-2 rounded-xl p-4 sm:p-6" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-base sm:text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Créer une campagne</h3>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#10B981]/10 border border-[#10B981]/20">
                    <CreditCard className="w-3.5 h-3.5 text-[#10B981]" />
                    <span className="text-sm font-bold text-[#10B981]">{credits.toFixed(2)}€</span>
                  </div>
                </div>

                {/* Type selector */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <button onClick={() => setCampType('email')}
                    className={`p-3 rounded-xl border flex items-center gap-2 transition-all ${campType === 'email' ? 'border-[#7C3AED] bg-[#7C3AED]/10' : 'border-white/10 bg-white/[0.03]'}`}
                    data-testid="camp-type-email">
                    <Mail className="w-5 h-5 text-[#7C3AED]" />
                    <div className="text-left">
                      <p className="text-sm text-white font-medium">Email</p>
                      <p className="text-[10px] text-[#94A3B8]">0,02€ / envoi</p>
                    </div>
                  </button>
                  <button onClick={() => setCampType('sms')}
                    className={`p-3 rounded-xl border flex items-center gap-2 transition-all ${campType === 'sms' ? 'border-[#F59E0B] bg-[#F59E0B]/10' : 'border-white/10 bg-white/[0.03]'}`}
                    data-testid="camp-type-sms">
                    <MessageSquare className="w-5 h-5 text-[#F59E0B]" />
                    <div className="text-left">
                      <p className="text-sm text-white font-medium">SMS</p>
                      <p className="text-[10px] text-[#94A3B8]">0,15€ / envoi</p>
                    </div>
                  </button>
                </div>

                {/* AI Generation */}
                <div className="p-4 rounded-xl bg-[#7C3AED]/5 border border-[#7C3AED]/15 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-[#F59E0B]" />
                    <p className="text-sm text-white font-semibold">Assistant IA (GPT-5.2)</p>
                  </div>
                  <div className="space-y-2.5">
                    <div>
                      <Label className="text-[#E2E8F0] text-xs">Thème de la campagne</Label>
                      <Input value={campTopic} onChange={e => setCampTopic(e.target.value)}
                        placeholder="Ex : Soldes d'été, nouveau produit, fête des mères..." className="mt-1 bg-white/5 border-white/10 text-white text-sm" data-testid="camp-topic" />
                    </div>
                    <div>
                      <Label className="text-[#E2E8F0] text-xs">Détails de la promotion (optionnel)</Label>
                      <Input value={campPromo} onChange={e => setCampPromo(e.target.value)}
                        placeholder="Ex : -30% sur tout le magasin, 1 acheté 1 offert..." className="mt-1 bg-white/5 border-white/10 text-white text-sm" data-testid="camp-promo" />
                    </div>
                    <div>
                      <Label className="text-[#E2E8F0] text-xs">Ton</Label>
                      <Select value={campTone} onValueChange={setCampTone}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A2E] border-white/10">
                          <SelectItem value="professionnel et engageant" className="text-white">Professionnel</SelectItem>
                          <SelectItem value="fun et décontracté" className="text-white">Fun et décontracté</SelectItem>
                          <SelectItem value="urgence et exclusivité" className="text-white">Urgence / Exclusif</SelectItem>
                          <SelectItem value="chaleureux et familial" className="text-white">Chaleureux / Familial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {campType === 'email' && (
                      <div>
                        <Label className="text-[#E2E8F0] text-xs">Images à intégrer</Label>
                        <div className="space-y-2 mt-1.5">
                          {campImages.map((url, i) => (
                            <div key={i} className="flex items-end gap-2">
                              <div className="flex-1">
                                <ImageUpload value={url} onChange={val => { const arr = [...campImages]; arr[i] = val; setCampImages(arr); }}
                                  label="" token={token} />
                              </div>
                              {campImages.length > 1 && !url && (
                                <button onClick={() => setCampImages(campImages.filter((_, j) => j !== i))} className="text-[#EF4444] hover:text-red-300 p-2"><Trash2 className="w-3.5 h-3.5" /></button>
                              )}
                            </div>
                          ))}
                          {campImages.length < 3 && (
                            <button onClick={() => setCampImages([...campImages, ''])} className="text-xs text-[#7C3AED] hover:text-[#A855F7] flex items-center gap-1">
                              <Plus className="w-3 h-3" /> Ajouter une image
                            </button>
                          )}
                        </div>
                        <p className="text-[10px] text-[#94A3B8] mt-1">Uploadez depuis votre téléphone ou ordinateur. Max 3 images, 5 Mo chacune.</p>
                      </div>
                    )}
                    <Button onClick={generateContent} disabled={campGenerating} className="btn-primary w-full gap-2" data-testid="camp-generate-btn">
                      {campGenerating ? <><Loader2 className="w-4 h-4 animate-spin" /> Génération en cours...</> : <><Sparkles className="w-4 h-4" /> Générer avec l&apos;IA</>}
                    </Button>
                    {campGenerating && (
                      <div className="mt-3 space-y-2">
                        {[
                          { step: 1, text: "Analyse du thème et de vos informations..." },
                          { step: 2, text: campType === 'email' ? "Rédaction du contenu HTML..." : "Rédaction du SMS..." },
                          { step: 3, text: "Intégration de vos liens et coordonnées..." },
                          { step: 4, text: "Finalisation et mise en forme..." }
                        ].map(s => (
                          <div key={s.step} className={`flex items-center gap-2 text-xs transition-all duration-500 ${campGenStep >= s.step ? 'text-[#E2E8F0]' : 'text-[#64748B]'}`}>
                            {campGenStep > s.step ? <Check className="w-3.5 h-3.5 text-[#10B981]" /> :
                             campGenStep === s.step ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#7C3AED]" /> :
                             <div className="w-3.5 h-3.5 rounded-full border border-[#64748B]" />}
                            {s.text}
                          </div>
                        ))}
                        <p className="text-[10px] text-[#94A3B8] text-center mt-1">Cela peut prendre 10-15 secondes</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Content editor */}
                {campType === 'email' && (
                  <div className="mb-3">
                    <Label className="text-[#E2E8F0] text-xs">Objet de l&apos;email</Label>
                    <Input value={campSubject} onChange={e => setCampSubject(e.target.value)}
                      placeholder="Ex : 🎉 Offre spéciale rien que pour vous !" className="mt-1 bg-white/5 border-white/10 text-white text-sm" data-testid="camp-subject" />
                  </div>
                )}
                <div className="mb-4">
                  <Label className="text-[#E2E8F0] text-xs">{campType === 'email' ? "Contenu HTML de l'email" : 'Texte du SMS'}</Label>
                  <textarea value={campContent} onChange={e => setCampContent(e.target.value)}
                    rows={campType === 'sms' ? 4 : 10}
                    className="mt-1 w-full bg-white/[0.06] border border-white/10 text-white text-sm rounded-xl p-3 resize-y focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] outline-none"
                    placeholder={campType === 'sms' ? 'Votre message SMS (max 160 caractères)...' : 'Le code HTML de votre email apparaîtra ici après génération IA...'}
                    data-testid="camp-content" />
                  {campType === 'sms' && <p className="text-[10px] text-[#94A3B8] mt-1">{campContent.length}/160 caractères</p>}
                </div>

                {/* Preview for email */}
                {campType === 'email' && campContent && (
                  <div className="mb-4">
                    <Label className="text-[#E2E8F0] text-xs mb-2 block">Aperçu</Label>
                    <div className="rounded-xl overflow-hidden bg-white max-h-[300px] overflow-y-auto">
                      {/* Email preview: DOMPurify sanitizes with strict whitelist — no scripts, no event handlers, no dangerous protocols */}
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(campContent, {
                        ALLOWED_TAGS: ['a','b','br','div','em','h1','h2','h3','h4','h5','h6','hr','i','img','li','ol','p','span','strong','table','tbody','td','tfoot','th','thead','tr','u','ul'],
                        ALLOWED_ATTR: ['href','src','alt','title','style','align','target','rel','width','height','border','cellpadding','cellspacing','colspan','rowspan'],
                        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
                        FORBID_TAGS: ['script','iframe','object','embed','form','input','button'],
                        FORBID_ATTR: ['onerror','onload','onclick','onmouseover','onfocus','onblur','onchange','onsubmit'],
                      }) }} />
                    </div>
                  </div>
                )}

                {/* Send test */}
                <div className="p-4 rounded-xl bg-[#F59E0B]/5 border border-[#F59E0B]/15 mb-4">
                  <p className="text-xs text-[#F59E0B] font-semibold mb-2">Envoyer un test gratuit</p>
                  <div className="flex gap-2">
                    <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} type="email"
                      placeholder="votre@email.com" className="bg-white/5 border-white/10 text-white text-sm flex-1" data-testid="test-email-input" />
                    <Button onClick={sendTestEmail} disabled={sendingTest || !campContent.trim()} variant="outline"
                      className="border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/10 flex-shrink-0 gap-1" data-testid="send-test-btn">
                      {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                      Tester
                    </Button>
                  </div>
                  <p className="text-[10px] text-[#94A3B8] mt-1.5">L&apos;email test est gratuit — vérifiez le rendu avant d&apos;envoyer à tous vos clients</p>
                </div>

                {/* Send */}
                <Button onClick={sendCampaign} disabled={campSending || !campContent.trim()}
                  className={`w-full rounded-xl py-5 text-base font-bold gap-2 ${campType === 'email' ? 'btn-primary' : 'btn-accent'}`} data-testid="camp-send-btn">
                  {campSending ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi en cours...</> : <><Send className="w-4 h-4" /> Envoyer la campagne</>}
                </Button>
                <p className="text-[10px] text-[#94A3B8] text-center mt-2">
                  Coût estimé : {campType === 'email' ? '0,02' : '0,15'}€ × {customerMeta.total || 0} clients = {((campType === 'email' ? 0.02 : 0.15) * (customerMeta.total || 0)).toFixed(2)}€
                </p>
              </div>

              {/* Right: Credits + History */}
              <div className="space-y-5">
                {/* Credits */}
                <div className="rounded-xl p-4 sm:p-5" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <h4 className="text-sm font-semibold text-white mb-3" style={{ fontFamily: 'Outfit' }}>Crédits campagne</h4>
                  <div className="text-center mb-4">
                    <p className="text-3xl font-black text-[#10B981]" style={{ fontFamily: 'Outfit' }}>{credits.toFixed(2)}€</p>
                    <p className="text-xs text-[#94A3B8]">Solde disponible</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[5, 10, 25].map(amt => (
                      <Button key={amt} onClick={() => buyCredits(amt)} variant="outline" size="sm"
                        className="border-white/10 text-white hover:bg-white/5 text-xs" data-testid={`buy-${amt}`}>
                        +{amt}€
                      </Button>
                    ))}
                  </div>
                  <div className="space-y-1 text-xs text-[#94A3B8]">
                    <div className="flex justify-between"><span>1 email</span><span className="text-[#10B981]">0,02€</span></div>
                    <div className="flex justify-between"><span>1 SMS</span><span className="text-[#10B981]">0,15€</span></div>
                  </div>
                </div>

                {/* Campaign History */}
                <div className="rounded-xl p-4 sm:p-5" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <h4 className="text-sm font-semibold text-white mb-3" style={{ fontFamily: 'Outfit' }}>Historique</h4>
                  <div className="space-y-2 max-h-[350px] overflow-y-auto">
                    {campaigns.length === 0 ? (
                      <p className="text-xs text-[#94A3B8] text-center py-4">Aucune campagne envoyée</p>
                    ) : campaigns.map((c) => (
                      <div key={c.id || c.created_at} className="p-3 rounded-lg bg-white/5">
                        <div className="flex items-center justify-between mb-1">
                          <Badge className={c.type === 'email' ? 'bg-[#7C3AED]/20 text-[#7C3AED]' : 'bg-[#F59E0B]/20 text-[#F59E0B]'}>
                            {c.type === 'email' ? 'Email' : 'SMS'}
                          </Badge>
                          <span className="text-xs text-[#94A3B8]">{new Date(c.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                        <p className="text-xs text-white">{c.sent_count} envoyé{c.sent_count > 1 ? 's' : ''}</p>
                        <p className="text-[10px] text-[#F59E0B]">Coût : {c.cost?.toFixed(2)}€</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
